<script setup>
import { computed } from 'vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import {
  getContactPageUrl,
  getInboxTroubleshootStepKeys,
} from 'dashboard/helper/inboxTroubleshootSteps';
import { useConfig } from 'dashboard/composables/useConfig';
import FacebookReauthorize from '../facebook/Reauthorize.vue';
import InstagramReauthorize from '../channels/instagram/Reauthorize.vue';
import WhatsappReauthorize from '../channels/whatsapp/Reauthorize.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  whatsappRegistrationIncomplete: {
    type: Boolean,
    default: false,
  },
});

const { hostURL } = useConfig();

const channelType = computed(() => props.inbox?.channel_type);

const provider = computed(() => props.inbox?.provider);

const isFacebookInbox = computed(() => channelType.value === INBOX_TYPES.FB);

const isInstagramInbox = computed(
  () => channelType.value === INBOX_TYPES.INSTAGRAM
);

const isEmbeddedSignupWhatsApp = computed(
  () => props.inbox?.provider_config?.source === 'embedded_signup'
);

const isWhatsAppCloudMetaEmbedded = computed(
  () =>
    channelType.value === INBOX_TYPES.WHATSAPP &&
    provider.value === 'whatsapp_cloud' &&
    isEmbeddedSignupWhatsApp.value
);

const stepKeys = computed(() => getInboxTroubleshootStepKeys(props.inbox));

const contactPageUrl = computed(() => getContactPageUrl(hostURL));

const stepTranslationKey = suffix => `INBOX_MGMT.TROUBLESHOOT.STEPS.${suffix}`;
</script>

<template>
  <div class="flex w-full flex-col gap-6">
    <div>
      <h2 class="text-base font-semibold text-n-slate-12">
        {{ $t('INBOX_MGMT.TROUBLESHOOT.TITLE') }}
      </h2>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.TROUBLESHOOT.SUBTITLE') }}
      </p>
    </div>

    <FacebookReauthorize v-if="isFacebookInbox" as-panel :inbox="inbox" />
    <InstagramReauthorize v-else-if="isInstagramInbox" as-panel />
    <WhatsappReauthorize
      v-else-if="isWhatsAppCloudMetaEmbedded"
      as-panel
      :inbox="inbox"
      :whatsapp-registration-incomplete="whatsappRegistrationIncomplete"
    />

    <ol class="list-decimal space-y-3 pl-5 text-sm text-n-slate-12">
      <li v-for="stepKey in stepKeys" :key="stepKey">
        {{ $t(stepTranslationKey(stepKey)) }}
      </li>
      <li>
        <span>{{ $t('INBOX_MGMT.TROUBLESHOOT.CONTACT.PREFIX') }}</span>
        {{ ' ' }}
        <a
          :href="contactPageUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-n-brand hover:underline"
        >
          {{ $t('INBOX_MGMT.TROUBLESHOOT.CONTACT.LINK') }}
        </a>
        <span>{{ $t('INBOX_MGMT.TROUBLESHOOT.CONTACT.SUFFIX') }}</span>
      </li>
    </ol>
  </div>
</template>
